// src/admin/pages/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminService } from "../../services/adminService";
import { Loader2 } from "lucide-react";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [courseDetails, setCourseDetails] = useState({});

  const ROLE_USER = "fe42ee60-9133-4f0e-b543-740d8697c23b";

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const usersData = await adminService.getUsers();
      const coursesData = await adminService.getCourses();
      const cohortsData = await adminService.getCohorts();

      setUsers(usersData);
      setCourses(coursesData);
      setCohorts(cohortsData);

      // Load each course detail
      let detailMap = {};
      for (const course of coursesData) {
        const detail = await adminService.getCourseDetail(course.id);
        detailMap[course.id] = detail;
      }
      setCourseDetails(detailMap);

    } catch (err) {
      console.error("Dashboard load error", err);
    } finally {
      setLoading(false);
    }
  };

  const totalUsers = users.length;
  const totalLearners = users.filter(u => u.user_role === ROLE_USER).length;
  const activeCohorts = cohorts.filter((c) => c.status === "active").length;

  return (
    <div className="space-y-10">

      {/* Title */}
      <h1 className="text-display">Admin Dashboard</h1>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center gap-3 text-[#54B0AF]">
          <Loader2 size={22} className="animate-spin" />
          <span>Loading dashboard…</span>
        </div>
      )}

      {/* ================================
          TOP STAT CARDS
      ================================= */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard label="Total Users" value={totalUsers} />
          <StatCard label="Total Learners" value={totalLearners} />
          <StatCard label="Total Courses" value={courses.length} />
          <StatCard label="Active Cohorts" value={activeCohorts} />
        </div>
      )}

      {/* ================================
          QUICK ACTIONS
      ================================= */}
      <div className="bg-white p-6 rounded-lg shadow stakeholder-summary-card flex gap-4">

        <Link
          to="/admin/users/create"
          className="px-4 py-2 bg-[#54B0AF] text-white rounded-md text-sm hover:opacity-90 shadow transition"
        >
          + Create User
        </Link>

        <Link
          to="/admin/waitlist"
          className="px-4 py-2 bg-[#54B0AF] text-white rounded-md text-sm hover:opacity-90 shadow transition"
        >
          View Waitlist
        </Link>

        <Link
          to="/admin/courses"
          className="px-4 py-2 bg-[#54B0AF] text-white rounded-md text-sm hover:opacity-90 shadow transition"
        >
          + Create Course
        </Link>

        <Link
          to="/admin/cohorts/create"
          className="px-4 py-2 bg-[#54B0AF] text-white rounded-md text-sm hover:opacity-90 shadow transition"
        >
          + Create Cohort
        </Link>

        <Link
          to="/admin/courses"
          className="px-4 py-2 bg-[#54B0AF] text-white rounded-md text-sm hover:opacity-90 shadow transition ml-auto"
        >
          Bulk Upload Learners →
        </Link>
      </div>

      {/* ================================
          COURSES OVERVIEW
      ================================= */}
      <div className="bg-white rounded-lg shadow stakeholder-summary-card p-6">
        <h2 className="text-title mb-4">Courses Overview</h2>

        {courses.length === 0 ? (
          <p className="text-muted">No courses available.</p>
        ) : (
          <table className="min-w-full text-[15px] border-collapse">
            <thead className="bg-gray-50">
              <tr className="border-b">
                <Th>Course</Th>
                <Th>Enrolled</Th>
                <Th>Coaches</Th>
                <Th>APs</Th>
                <Th>Cohorts</Th>
                <Th></Th>
              </tr>
            </thead>

            <tbody>
              {courses.map((c) => {
                const detail = courseDetails[c.id];

                const learners = detail?.learners?.length || 0;
                const courseCoaches = detail?.coaches?.length || 0;
                const courseAps = detail?.accountability_partners?.length || 0;

                const courseCohorts = cohorts.filter(
                  (co) => String(co.course_id) === String(c.id)
                ).length;

                return (
                  <tr key={c.id} className="border-b hover:bg-gray-100 transition">
                    <Td>{c.title}</Td>
                    <Td>{learners}</Td>
                    <Td>{courseCoaches}</Td>
                    <Td>{courseAps}</Td>
                    <Td>{courseCohorts}</Td>

                    <Td className="text-right">
                      <Link
                        to={`/admin/courses/${c.id}`}
                        className="text-[#54B0AF] font-semibold hover:underline mr-4"
                      >
                        View
                      </Link>

                      <Link
                        to={`/admin/courses/${c.id}/bulk-upload`}
                        className="text-[#54B0AF] font-semibold hover:underline"
                      >
                        Upload
                      </Link>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ================================
          COHORTS OVERVIEW
      ================================= */}
      <div className="bg-white rounded-lg shadow stakeholder-summary-card p-6">
        <h2 className="text-title mb-4">Cohorts Overview</h2>

        {cohorts.length === 0 ? (
          <p className="text-muted">No cohorts available.</p>
        ) : (
          <table className="min-w-full text-[15px] border-collapse">
            <thead className="bg-gray-50">
              <tr className="border-b">
                <Th>Cohort</Th>
                <Th>Course</Th>
                <Th>Start Date</Th>
                <Th>Learners</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>

            <tbody>
              {cohorts.map((cohort) => {
                const course = courses.find((c) => c.id === cohort.course_id);

                const learnersInCohort =
                  (courseDetails[cohort.course_id]?.learners || [])
                    .filter((l) => String(l.cohort_id) === String(cohort.id))
                    .length;

                return (
                  <tr key={cohort.id} className="border-b hover:bg-gray-100 transition">
                    <Td>{cohort.name}</Td>
                    <Td>{course?.title || "—"}</Td>
                    <Td>{cohort.start_date}</Td>
                    <Td>{learnersInCohort}</Td>
                    <Td className="capitalize">{cohort.status}</Td>

                    <Td className="text-right">
                      <Link
                        to={`/admin/cohorts/${cohort.id}`}
                        className="text-[#54B0AF] font-semibold hover:underline"
                      >
                        View
                      </Link>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Dashboard;

const StatCard = ({ label, value }) => (
  <div className="bg-white shadow rounded-lg p-6 text-center stakeholder-summary-card">
    <p className="text-muted">{label}</p>
    <p className="text-2xl font-semibold text-[#323232] mt-1">{value}</p>
  </div>
);

const Th = ({ children }) => (
  <th className="p-2 text-left text-title font-semibold">{children}</th>
);

const Td = ({ children, className = "" }) => (
  <td className={`p-2 ${className}`}>{children}</td>
);
